import { SystemResponse } from '@kcftech/prometheus-client';
import { QueryObserverResult, skipToken, useQuery } from '@tanstack/react-query';
import { dayMs, minuteMs } from 'components/date_time/time';
import { sdClient } from 'contexts/SDKClientContext';
import { getErrorStatus } from 'utils/api';
import { queryClient } from 'utils/query';

export type UseSystemOptions = Readonly<{
    /**
     * If the query should be executed (defaults to `true`).
     */
    executeQuery?: boolean;
}>;

/**
 * Loads the system for the supplied system id. Resolves to `null` when the
 * system doesn't exist (e.g. it was deleted).  If a location id is supplied
 * the system must belong to that location or undefined will be returned.
 *
 * @param systemId The system to load.
 * @param locationId Optional location check for the system.
 * @param executeQuery If the query should be executed.
 */
export function useSystem(
    systemId: string | null | undefined,
    options?: UseSystemOptions,
): QueryObserverResult<SystemResponse | null, Partial<Error>> {
    const executeQuery = options?.executeQuery ?? true;

    return useQuery({
        queryKey: ['get-system', systemId],

        queryFn: !executeQuery || !systemId ? skipToken : () => fetchSystem(systemId),

        gcTime: 2 * dayMs,
        staleTime: 30 * minuteMs,
    });
}

export function invalidateSystemQuery(systemId: string): void {
    queryClient.invalidateQueries({ queryKey: ['get-system', systemId] });
}

/**
 * Treating missing systems as a definitive answer, rather than a failure because
 * surfacing it as an error makes React Query retry it and refetch it whenever a
 * new observer mounts, which causes issues for some views. (Mission Control)
 */
async function fetchSystem(systemId: string): Promise<SystemResponse | null> {
    try {
        return await sdClient.getSystem({ systemId });
    } catch (error) {
        if (getErrorStatus(error) === 404) {
            return null;
        }
        throw error;
    }
}
